import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Stack,
  Text,
  UnstyledButton,
  Group,
  Collapse,
  ThemeIcon,
  Badge,
  ScrollArea,
  Drawer
} from '@mantine/core';
import {
  IconLayoutDashboard,
  IconMasksTheater,
  IconUsersGroup,
  IconSparkles,
  IconShoppingBag,
  IconHeart,
  IconUsers,
  IconCreditCard,
  IconChartBar,
  IconWorld,
  IconTool,
  IconClipboardList,
  IconChevronRight,
  IconUpload
} from '@tabler/icons-react';
import { useAuth } from '../contexts/AuthContext';
import { friendsAPI } from '../services/api';

const Sidebar = ({ open, onClose }) => {
  const location = useLocation();
  const { user, isAdmin } = useAuth();

  // Check if we're on an admin page
  const isAdminPage = location.pathname.startsWith('/admin');

  // State for expanded menu sections
  const [openAdmin, setOpenAdmin] = useState(true);

  // State for friends count
  const [friendsCount, setFriendsCount] = useState(0);

  // Scroll position persistence
  const scrollAreaRef = useRef();
  const saved = sessionStorage.getItem('sidebarScrollPosition');
  const scrollPositionRef = useRef(saved ? parseInt(saved, 10) : 0);

  // Restore scroll position on mount
  useLayoutEffect(() => {
    if (scrollAreaRef.current && scrollPositionRef.current > 0) {
      scrollAreaRef.current.scrollTop = scrollPositionRef.current;
    }
  }, []);

  // Fetch friends count on component mount
  useEffect(() => {
    const loadFriendsCount = async () => {
      try {
        const response = await friendsAPI.getFriends();
        setFriendsCount(response.data.length);
      } catch (error) {
        console.warn('Failed to load friends count:', error);
        setFriendsCount(0);
      }
    };

    if (user) {
      loadFriendsCount();
    }
  }, [user]);

  const adminSections = isAdmin ? [
    {
      label: 'Administration',
      collapsible: true,
      expanded: openAdmin,
      onToggle: () => setOpenAdmin(!openAdmin),
      icon: IconTool,
      title: 'Admin Tools',
      items: [
        {
          icon: IconUsers,
          label: 'User Management',
          path: '/admin/users',
          color: 'red'
        },
        {
          icon: IconCreditCard,
          label: 'Subscriptions',
          path: '/admin/subscriptions',
          color: 'red'
        },
        {
          icon: IconChartBar,
          label: 'Analytics',
          path: '/admin/analytics',
          color: 'red'
        },
        {
          icon: IconWorld,
          label: 'Environment',
          path: '/admin/environment',
          color: 'red'
        },
        {
          icon: IconTool,
          label: 'System Updates',
          path: '/admin/system',
          color: 'red'
        },
        {
          icon: IconClipboardList,
          label: 'Waitlist',
          path: '/admin/waitlist',
          color: 'red'
        },
        {
          icon: IconUpload,
          label: 'Import Products',
          path: '/admin/products/import',
          color: 'red'
        }
      ]
    }
  ] : [];

  const menuSections = [
    ...(isAdmin ? adminSections : []),
    {
      items: [
        {
          icon: IconLayoutDashboard,
          label: 'Dashboard',
          path: '/',
          color: 'blue'
        },
        {
          icon: IconMasksTheater,
          label: 'Personas',
          path: '/personas',
          color: 'cyan'
        },
        {
          icon: IconUsersGroup,
          label: 'Groups',
          path: '/groups',
          color: 'teal'
        },
        {
          icon: IconSparkles,
          label: 'AI Gift Concierge',
          path: '/ai-concierge',
          color: 'violet',
          badge: 'Pro',
          badgeSize: 'xs'
        },
        {
          icon: IconShoppingBag,
          label: 'Browse Products',
          path: '/products',
          color: 'grape'
        },
        {
          icon: IconHeart,
          label: 'My Wishlists',
          path: '/wishlists',
          color: 'grape'
        },
        {
          icon: IconUsers,
          label: 'Friends',
          path: '/friends',
          color: 'orange',
          badge: friendsCount > 0 ? friendsCount.toString() : null,
          badgeSize: 'xs'
        }
      ]
    }
  ];

  const NavItem = ({ icon, label, path, color, badge, badgeSize, onClick }) => {
    const isActive = location.pathname === path;

    return (
      <UnstyledButton
        component={path ? Link : 'button'}
        to={path}
        onClick={() => {
          onClick?.();
          onClose();
        }}
        style={{
          width: '100%',
          padding: '10px 12px',
          borderRadius: '10px',
          transition: 'all 0.2s ease',
          background: isActive
            ? 'linear-gradient(135deg, rgba(25,113,194,0.15) 0%, rgba(21,170,191,0.1) 100%)'
            : 'transparent',
          border: isActive ? '1px solid rgba(25,113,194,0.3)' : '1px solid transparent',
          position: 'relative',
          overflow: 'hidden'
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = 'transparent';
          }
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon
              size="md"
              radius="md"
              variant={isActive ? 'gradient' : 'light'}
              gradient={isActive ? { from: 'blue', to: 'cyan', deg: 45 } : undefined}
              color={color}
              style={{
                border: isActive ? '2px solid rgba(25,113,194,0.3)' : 'none'
              }}
            >
              {React.createElement(icon, { size: 15, stroke: 1.75 })}
            </ThemeIcon>
            <Text
              size="sm"
              fw={isActive ? 600 : 500}
              c={isActive ? 'white' : 'gray.3'}
              style={{ flex: 1 }}
            >
              {label}
            </Text>
          </Group>
          {badge && (
            badge === 'Pro' ? (
              <Badge
                size={badgeSize || 'sm'}
                variant="gradient"
                gradient={{ from: 'violet', to: 'grape', deg: 90 }}
              >
                {badge}
              </Badge>
            ) : (
              <Badge size={badgeSize || 'sm'} variant="light" color="gray">
                {badge}
              </Badge>
            )
          )}

        </Group>
      </UnstyledButton>
    );
  };

  const CollapsibleSection = ({ icon, title, expanded, onToggle, items }) => (
    <Box>
      <UnstyledButton
        onClick={onToggle}
        style={{
          width: '100%',
          padding: '10px 12px',
          borderRadius: '10px',
          transition: 'all 0.2s ease',
          background: expanded ? 'rgba(255, 255, 255, 0.05)' : 'transparent'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = expanded ? 'rgba(255, 255, 255, 0.05)' : 'transparent';
        }}
      >
        <Group justify="space-between">
          <Group gap="sm">
            {React.createElement(icon, { size: 16, stroke: 1.75 })}
            <Text size="sm" fw={600} c="white">
              {title}
            </Text>
          </Group>
          <IconChevronRight
            size={13}
            style={{ transition: 'transform 0.2s ease', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
          />
        </Group>
      </UnstyledButton>
      <Collapse in={expanded}>
        <Stack gap={4} mt={4} ml="xs">
          {items.map((item, idx) => (
            <NavItem key={idx} {...item} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );

  const SidebarContent = () => (
    <Box
      style={{
        height: '100%',
        background: isAdminPage
          ? 'linear-gradient(180deg, #2d1b1b 0%, #1a0f0f 100%)'
          : 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Admin-mode indicator only — regular branding already lives in
          Navbar.js, and "Signed in as" duplicated the Navbar's own user
          menu, so neither is repeated here. */}
      {isAdminPage && (
        <Box
          p="md"
          style={{
            background: 'linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(0,0,0,0) 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <Group gap="sm">
            <ThemeIcon size={30} radius="md" variant="gradient" gradient={{ from: 'red', to: 'orange', deg: 45 }}>
              <IconTool size={16} />
            </ThemeIcon>
            <Box>
              <Text size="sm" fw={700} c="white" style={{ lineHeight: 1.2 }}>Admin Panel</Text>
              <Text size="xs" c="dimmed" style={{ lineHeight: 1 }}>System management</Text>
            </Box>
          </Group>
        </Box>
      )}

      {/* Navigation */}
      <ScrollArea
        style={{ flex: 1 }}
        p="md"
        viewportRef={(el) => {
          scrollAreaRef.current = el;
          if (el && scrollPositionRef.current > 0) {
            el.scrollTop = scrollPositionRef.current;
          }
        }}
        onScrollPositionChange={({ y }) => {
          scrollPositionRef.current = y;
          sessionStorage.setItem('sidebarScrollPosition', y.toString());
        }}
      >
        <Stack gap="lg">
          {menuSections.map((section, idx) => (
            <Box key={idx}>
              {section.label && (
                <Text
                  size="xs"
                  tt="uppercase"
                  fw={700}
                  c="dimmed"
                  mb="xs"
                  ml="xs"
                  style={{ letterSpacing: '0.5px' }}
                >
                  {section.label}
                </Text>
              )}
              <Stack gap={4}>
                {section.collapsible ? (
                  <CollapsibleSection
                    icon={section.icon}
                    title={section.title}
                    expanded={section.expanded}
                    onToggle={section.onToggle}
                    items={section.items}
                  />
                ) : (
                  section.items.map((item, itemIdx) => (
                    <NavItem key={itemIdx} {...item} />
                  ))
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      </ScrollArea>

      {/* Footer */}
      <Box
        p="md"
        style={{
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 100%)'
        }}
      >
        <Box
          p="sm"
          style={{
            background: 'linear-gradient(135deg, rgba(25,113,194,0.1) 0%, rgba(21,170,191,0.1) 100%)',
            borderRadius: '8px',
            border: '1px solid rgba(25,113,194,0.2)'
          }}
        >
          <Group gap="xs" mb={4}>
            <ThemeIcon size="sm" radius="xl" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>
              <IconSparkles size={11} />
            </ThemeIcon>
            <Text size="xs" fw={600} c="white">
              Pro Tip
            </Text>
          </Group>
          <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
            Create detailed personas for better AI gift recommendations
          </Text>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        opened={open}
        onClose={onClose}
        size={280}
        padding={0}
        styles={{
          body: { padding: 0, height: '100%' },
          content: { background: 'transparent' }
        }}
        hiddenFrom="md"
      >
        <SidebarContent />
      </Drawer>

      {/* Desktop Sidebar */}
      <Box
        visibleFrom="md"
        style={{
          width: 280,
          position: 'fixed',
          left: 0,
          top: 64,
          height: 'calc(100vh - 64px)',
          zIndex: 100,
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        <SidebarContent />
      </Box>
    </>
  );
};

export default Sidebar;
